import { useState } from 'react';
import { useIntl } from 'react-intl';

// eslint-disable-next-line @nx/enforce-module-boundaries
import {
  useGetConceptGroupUsageQuery,
  useDeleteConceptGroupMutation,
  useConceptGroups,
} from '@verantos-dev/data-access-concept-groups';
import { ApiWorkflowDialogMessages, DialogButton } from '@verantos-dev/shared-components';
import { ConceptGroup } from '@verantos-dev/types-concepts';

import {
  useConceptGroupsListDialogContext,
  ConceptGroupsDialogActionType,
} from '../context/ConceptGroupsListDialogContext';

export const useDeleteConceptGroupModal = () => {
  const intl = useIntl();
  const { state, dispatch } = useConceptGroupsListDialogContext();
  const { conceptGroups } = useConceptGroups();

  const conceptGroupId = state.data?.conceptGroupId;

  const conceptGroupDetails = conceptGroups.find(
    (conceptGroup) => conceptGroup.conceptGroupId === conceptGroupId
  ) as ConceptGroup | undefined;

  const { data: usageData, isLoading: checkingUsage } = useGetConceptGroupUsageQuery(
    { conceptGroupId: conceptGroupId || '' },
    { skip: !conceptGroupId }
  );
  const [deleteMutation, { isLoading: deleting, error: deleteError }] =
    useDeleteConceptGroupMutation();

  const [saveButtonColor, setSaveButtonColor] = useState<DialogButton['color']>('destructive');

  const isInUse = usageData?.data?.isUsed || false;
  const usageCount = usageData?.data?.usageCount || 0;
  const conceptGroupName = conceptGroupDetails?.conceptGroupName || '';

  const closeDialog = () => {
    dispatch({ type: ConceptGroupsDialogActionType.CLOSE_DIALOG });
    setSaveButtonColor('destructive');
  };

  const onSubmit = async () => {
    if (!conceptGroupId || isInUse) return;

    // Proceed with deletion only if not in use
    await deleteMutation({ conceptGroupId });
    setSaveButtonColor('primary');
  };

  const contentMessages = {
    title: isInUse
      ? intl.formatMessage({ id: 'conceptGroups.deleteConceptGroupModal.inUseTitle' })
      : intl.formatMessage({ id: 'conceptGroups.deleteConceptGroupModal.title' }),
    description: intl.formatMessage(
      { id: 'conceptGroups.deleteConceptGroupModal.description' },
      { conceptGroupName: <b>{conceptGroupName}</b> }
    ),
    confirmTitle: intl.formatMessage({ id: 'conceptGroups.deleteConceptGroupModal.confirmTitle' }),
    actionUndone: intl.formatMessage({ id: 'conceptGroups.deleteConceptGroupModal.actionUndone' }),
    inUseError: intl.formatMessage({ id: 'conceptGroups.deleteConceptGroupModal.inUseError' }),
    inUseDescription: intl.formatMessage({
      id: 'conceptGroups.deleteConceptGroupModal.inUseDescription',
    }),
    usageCount,
  };

  const dialogMessages: ApiWorkflowDialogMessages = {
    loadingCancelButtonText: intl.formatMessage({
      id: 'conceptGroups.deleteConceptGroupModal.cancelButton',
    }),
    loadingSaveButtonText: intl.formatMessage({
      id: 'conceptGroups.deleteConceptGroupModal.deleteButton',
    }),
    entryCancelButtonText: intl.formatMessage({
      id: 'conceptGroups.deleteConceptGroupModal.cancelButton',
    }),
    entrySaveButtonText: intl.formatMessage({
      id: 'conceptGroups.deleteConceptGroupModal.deleteButton',
    }),
    submittingCancelButtonText: intl.formatMessage({
      id: 'conceptGroups.deleteConceptGroupModal.cancelButton',
    }),
    submittingSaveButtonText: intl.formatMessage({
      id: 'conceptGroups.deleteConceptGroupModal.submitting',
    }),
    successCloseButtonText: intl.formatMessage({
      id: 'conceptGroups.deleteConceptGroupModal.closeButton',
    }),
    submittingErrorTitle: intl.formatMessage({
      id: 'conceptGroups.deleteConceptGroupModal.submittingError',
    }),
    successTitle: intl.formatMessage({
      id: 'conceptGroups.deleteConceptGroupModal.success.title',
    }),
    successText: intl.formatMessage(
      { id: 'conceptGroups.deleteConceptGroupModal.success.text' },
      { conceptGroupName }
    ),
  };

  return {
    dialogMessages,
    contentMessages,
    isInUse,
    isSubmitting: deleting,
    deleteConceptGroupError: deleteError,
    isInitialLoading: checkingUsage,
    saveButtonColor,
    isValidForSubmit: !checkingUsage && !deleting && !isInUse,
    onClose: closeDialog,
    onSubmit,
  };
};
